import {
  collection, doc, getDocs, orderBy, query, runTransaction, serverTimestamp, Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { logAuditEvent } from './auditService';
import { Store, StoreOrder, StoreOrderItem, StoreOrderStatus } from '../types';

interface CreateStoreOrderInput {
  store: Store;
  requestedBy: string;
  requestedByEmail: string;
  requestedByName: string;
  notes?: string;
  items: StoreOrderItem[];
}

export async function createStoreOrder(input: CreateStoreOrderInput): Promise<string> {
  const ref = doc(collection(db, 'store_orders'));
  const year = new Date().getFullYear();
  const counterRef = doc(db, 'counters', 'store_orders_' + year);
  let orderNumber = '';

  await runTransaction(db, async (transaction) => {
    const counterSnap = await transaction.get(counterRef);
    const nextOrderSequence = ((counterSnap.data()?.lastNumber ?? 0) as number) + 1;
    orderNumber = 'ZAM/' + year + '/' + nextOrderSequence;

    transaction.set(counterRef, {
      year,
      lastNumber: nextOrderSequence,
      updatedAt: serverTimestamp(),
    }, { merge: true });

    transaction.set(ref, {
      orderNumber,
      storeId: input.store.id,
      storeName: input.store.name,
      requestedBy: input.requestedBy,
      requestedByEmail: input.requestedByEmail,
      requestedByName: input.requestedByName,
      notes: input.notes?.trim() || null,
      items: input.items,
      status: 'pending' as StoreOrderStatus,
      createdAt: serverTimestamp(),
    });
  });

  logAuditEvent({
    entityType: 'store_order',
    entityId: ref.id,
    action: 'create',
    details: orderNumber + ' — ' + input.store.name,
  }).catch(() => {});
  return ref.id;
}

export async function getStoreOrders(): Promise<StoreOrder[]> {
  const snap = await getDocs(query(collection(db, 'store_orders'), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => {
    const data = d.data();
    return {
      id: d.id,
      orderNumber: data.orderNumber,
      storeId: data.storeId,
      storeName: data.storeName,
      requestedBy: data.requestedBy,
      requestedByEmail: data.requestedByEmail ?? '',
      requestedByName: data.requestedByName ?? data.requestedByEmail ?? '',
      notes: data.notes ?? undefined,
      items: data.items ?? [],
      status: data.status,
      downloadUrl: data.downloadUrl ?? undefined,
      errorMessage: data.errorMessage ?? undefined,
      createdAt: (data.createdAt as Timestamp)?.toDate?.() ?? new Date(),
    } as StoreOrder;
  });
}
